import React, { useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  Users,
  CheckCircle2,
  Clock,
  AlertCircle,
  Ban,
  Search,
  Download,
  X,
  Pencil,
  Trash2,
  ChevronLeft,
  MessageSquare,
  Clapperboard,
  Coins,
  Video,
  Mic,
  Image as ImageIcon,
  Info,
  ClipboardCheck,
  Repeat,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  getSyllabusMatrix,
  saveSyllabusEntries,
  deleteSyllabusEntry,
  getSyllabusRemark,
  saveSyllabusRemark,
  getSyllabusSubmission,
  gradeSyllabusSubmission,
  getSyllabusSubcategories,
  savePlanRefusal,
  deletePlanRefusal,
} from '../../api';
import showToast from '../../utils/toast';
import {
  SYLLABUS_TOPICS,
  STATUS_OPTIONS,
  getScoreBand,
  getEntryStatus,
  getMonthYearOptions,
  gradeCoinOptions,
  ruleCoinOptions,
  activityName,
  splitScore,
  joinScore,
  MAX_SESSION_SCORE,
} from '../../utils/syllabusTopics';
import { buildSubcategoryNodes } from '../../utils/courseSubcategoryNodes';
import {
  topicScope,
  scopeFor,
  pickerCategories,
  pickerLevels,
  chainFor,
  defaultPath,
  findTopicPick,
  categoryScopes,
} from '../../utils/syllabusTrackerScopes';
import SearchableSelect from '../common/SearchableSelect';
import PracticeRecordModal from './PracticeRecordModal';

const STATUS_ICON = {
  all: Users,
  completed: CheckCircle2,
  in_progress: Clock,
  not_started: AlertCircle,
  not_applicable: Ban,
};

const ITEMS_PER_PAGE = 20;

const formatMonthYearLabel = (month, year) =>
  new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

// The columns are the admin's course content, named after it (Add New Content, e.g. "Action Song 3").
// An older column kept because it holds marks has a coach's old name for it, or "<prefix> <n>" for
// the scope shown (utils/syllabusTrackerScopes.js).
const getColumnLabel = (scope, session) =>
  session.contentTitle || session.label || `${scope.prefix} ${session.sessionNumber}`;

// Each subcategory numbers its own content ("Action Song 1" in Vocal and in Instrumental Songs), so a
// column name shown more than once in a view also names the subcategory its content is in
// (`rowLabel`): rowOf(session) is that name for such a column, else "".
const repeatedRows = (scope, sessions) => {
  const times = new Map();
  sessions.forEach((s) => times.set(getColumnLabel(scope, s), (times.get(getColumnLabel(scope, s)) || 0) + 1));
  return (session) => (session && times.get(getColumnLabel(scope, session)) > 1 ? session.rowLabel || '' : '');
};
const withRow = (label, row) => (row ? `${label} · ${row}` : label);

const formatEntryDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatSubmissionTimestamp = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

// A cell with no score yet (not N/A): its chip shows a coins icon for the coach.
const isUnscored = (cell) => !cell.isNotApplicable && (cell.score === null || cell.score === undefined);

const SUBMISSION_TYPE_ICON = { video: Video, audio: Mic, image: ImageIcon };
const SUBMISSION_TYPE_LABEL = { video: 'Video', audio: 'Audio', image: 'Photo' };

function SubmissionTypeIcon({ type, size = 13 }) {
  const IconComp = SUBMISSION_TYPE_ICON[type] || Video;
  return <IconComp size={size} />;
}

// A coin rule's name as the admin typed it, starting with a capital ("expression" -> "Expression").
const ruleName = (label = '') => label.charAt(0).toUpperCase() + label.slice(1);

// "Expression 15 · Representation 4": the coin rules' part of a saved score.
const ruleScoresText = (ruleScores = []) =>
  ruleScores.map((entry) => `${ruleName(entry.label)} ${entry.score}`).join(' · ');

// An activity's quiz coins (the matrix's `coinParts.quizzes`) aren't the coach's to give: the child
// gets them for correct answers on the quiz's first try.
function QuizCoinsNote({ parts }) {
  const quizzes = parts?.quizzes || [];
  if (!quizzes.length) return null;
  const coins = quizzes.reduce((sum, quiz) => sum + quiz.coins, 0);
  const names = quizzes.map((quiz) => (quiz.title ? `"${quiz.title}"` : 'the quiz')).join(', ');
  return (
    <p className="syllabus-quiz-note">
      <Coins size={14} aria-hidden="true" />
      <span>
        Quiz {names}: {coins} coins, not given here. The child gets them for correct answers on the
        quiz&apos;s first try; later tries are practice.
      </span>
    </p>
  );
}

// The coach's coins for an activity whose chapter has coin rules (`parts`, the matrix's
// `coinParts`), block by block: the activity's grade (its PDF, audio, video, game ...) and each rule
// (e.g. Expression), each up to its admin coins. `split` = { activity, rules: { [label]: coins } }
// (null = not picked, utils/syllabusTopics.js splitScore). `mode`: "buttons" (the submission popup,
// with a box to type any amount as well) or "inputs" (the score popup).
function CoinPartBlocks({ parts, split, onChange, mode, disabled = false }) {
  const blocks = [
    ...(parts.activity > 0
      ? [{
        key: 'activity',
        name: activityName(parts.activityTypes),
        kind: 'Activity',
        max: parts.activity,
        value: split.activity,
        options: gradeCoinOptions(parts.activity),
        set: (value) => onChange({ ...split, activity: value }),
      }]
      : []),
    ...parts.rules.map((rule) => ({
      key: `rule:${rule.label}`,
      name: ruleName(rule.label),
      kind: 'Coin rule',
      max: rule.coins,
      value: split.rules[rule.label] ?? null,
      options: ruleCoinOptions(rule.coins),
      set: (value) => onChange({ ...split, rules: { ...split.rules, [rule.label]: value } }),
    })),
  ];
  const toNumber = (text) => (text === '' ? null : Number(text));

  return (
    <div className="syllabus-coin-parts">
      {blocks.map((block) => {
        const over = block.value !== null && (block.value > block.max || block.value < 0);
        return (
          <div
            key={block.key}
            className="syllabus-coin-part"
            {...(mode === 'buttons' ? { role: 'group', 'aria-label': `${block.name} coins` } : {})}
          >
            <div className="syllabus-coin-part-head">
              <span className="syllabus-coin-part-name">{block.name}</span>
              <span className="syllabus-coin-part-max">{block.kind} · up to {block.max} coins</span>
            </div>
            {mode === 'buttons' ? (
              <>
                <div className="syllabus-submission-grade-options">
                  {block.options.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={block.value === value ? 'active' : ''}
                      aria-label={`${block.name}: ${value} coins`}
                      aria-pressed={block.value === value}
                      disabled={disabled}
                      onClick={() => block.set(value)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <label className="syllabus-submission-grade-custom">
                  <span>Or type the coins</span>
                  <input
                    type="number"
                    min={0}
                    max={block.max}
                    step={1}
                    value={block.value ?? ''}
                    disabled={disabled}
                    onChange={(e) => block.set(toNumber(e.target.value))}
                    placeholder={`0-${block.max}`}
                    aria-label={`${block.name} coins, typed`}
                  />
                </label>
              </>
            ) : (
              <input
                type="number"
                className="syllabus-coin-part-input"
                min={0}
                max={block.max}
                value={block.value ?? ''}
                disabled={disabled}
                onChange={(e) => block.set(toNumber(e.target.value))}
                placeholder={`0-${block.max}`}
                aria-label={`${block.name} coins`}
              />
            )}
            {over && (
              <p className="syllabus-score-limit is-over" role="alert">
                {block.name} can get 0 to {block.max} coins (the admin&apos;s limit).
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Whether a split (CoinPartBlocks) holds a number outside its block's limit.
const splitOutOfRange = (parts, split) =>
  [
    [split.activity, parts.activity],
    ...parts.rules.map((rule) => [split.rules[rule.label], rule.coins]),
  ].some(([value, max]) => value !== null && value !== undefined && (Number.isNaN(value) || value < 0 || value > max));

// Child detail panel: a per-child, per-topic activity sheet (Scene/Coins/
// Status/Coach Comment/Date, plus per-row edit/delete when canEdit) with its
// own Topic tabs so a coach can flip between the picked category's topics
// (English: Action Songs, Scenes, Poem ...) for this child without leaving
// the panel. It fetches its own data (matrix + remarks) per active topic
// rather than reusing the parent page's — same self-fetching pattern as the
// rest of this page — and only refetches the parent's grid (onSaved) when
// the edited topic is the one currently shown behind it. A picked
// subcategory that narrows a topic (Action Songs › Vocal Songs) gets the
// first tab. The activities are the admin's course content, so none are
// added here.
function ChildDetailModal({
  studentId,
  studentName,
  balagruhaName,
  balagruhaId,
  initialScope,
  outerScope,
  categoryTabs,
  month,
  year,
  canEdit,
  onClose,
  onSaved,
}) {
  const [activeScope, setActiveScope] = useState(initialScope);
  const activeTopic = activeScope.topic;
  const tabs = categoryTabs.some((tab) => tab.id === initialScope.id)
    ? categoryTabs
    : [initialScope, ...categoryTabs];
  const [sessions, setSessions] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [remarks, setRemarks] = useState({ text: '', updatedAt: null });
  const [remarksDraft, setRemarksDraft] = useState('');
  const [savingRemarks, setSavingRemarks] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [deletingSessionNumber, setDeletingSessionNumber] = useState(null);
  const rowOf = repeatedRows(activeScope, sessions);

  const loadTopicData = async (scopeToLoad) => {
    setLoading(true);
    try {
      const [matrixRes, remarkRes] = await Promise.all([
        getSyllabusMatrix({ balagruhaId, topic: scopeToLoad.topic, month, year, subcategories: scopeToLoad.subcategories }),
        getSyllabusRemark({ balagruhaId, topic: scopeToLoad.topic, month, year, studentId }),
      ]);
      setSessions(matrixRes?.data?.sessions || []);
      const child = (matrixRes?.data?.children || []).find((c) => c.studentId === studentId);
      setEntries(child?.sessions || []);
      const remarksText = remarkRes?.data?.remarks || '';
      setRemarks({ text: remarksText, updatedAt: remarkRes?.data?.updatedAt || null });
      setRemarksDraft(remarksText);
    } catch (error) {
      showToast('Failed to load syllabus details', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTopicData(activeScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScope.id]);

  // Refresh this panel's own data, and only poke the parent grid when the
  // change happened on the topic it's actually displaying.
  const refresh = () => {
    loadTopicData(activeScope);
    if (activeScope.id === outerScope.id) onSaved?.();
  };

  const handleDelete = async (sessionNumber) => {
    if (!window.confirm('Clear this activity for this child?')) return;
    setDeletingSessionNumber(sessionNumber);
    try {
      await deleteSyllabusEntry({ balagruhaId, topic: activeTopic, month, year, studentId, sessionNumber });
      showToast('Entry cleared', 'success');
      refresh();
    } catch (error) {
      showToast(error?.response?.data?.message || 'Failed to clear entry', 'error');
    } finally {
      setDeletingSessionNumber(null);
    }
  };

  const saveRemarksDraft = async () => {
    if (savingRemarks || remarksDraft === remarks.text) return;
    setSavingRemarks(true);
    try {
      const res = await saveSyllabusRemark({ balagruhaId, topic: activeTopic, month, year, studentId, remarks: remarksDraft });
      setRemarks({ text: res?.data?.remarks || '', updatedAt: res?.data?.updatedAt || null });
      showToast('Remarks saved', 'success');
    } catch (error) {
      showToast(error?.response?.data?.message || 'Failed to save remarks', 'error');
    } finally {
      setSavingRemarks(false);
    }
  };

  const totalCoins = entries.reduce((sum, e) => sum + (e.isNotApplicable ? 0 : (e.score || 0)), 0);
  // Each activity's most coins: its admin limit, else 30.
  const maxCoins = sessions.reduce((sum, s) => sum + (s.maxScore ?? MAX_SESSION_SCORE), 0);
  const percent = maxCoins > 0 ? Math.round((totalCoins / maxCoins) * 100) : 0;
  const columnCount = canEdit ? 6 : 5;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="syllabus-detail-panel">
          <div className="syllabus-detail-header">
            <button type="button" className="syllabus-detail-back" onClick={onClose} aria-label="Back">
              <ChevronLeft size={18} />
            </button>
            <div className="syllabus-detail-avatar" aria-hidden="true">
              {(studentName || '?').charAt(0).toUpperCase()}
            </div>
            <div className="syllabus-detail-heading">
              <h3>{studentName}</h3>
              {balagruhaName && <div className="syllabus-detail-subtitle">{balagruhaName}</div>}
            </div>
            <div className="syllabus-detail-header-actions">
              <button type="button" className="syllabus-modal-close" onClick={onClose} aria-label="Close">
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="syllabus-detail-topic-tabs" role="group" aria-label="Topics">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={activeScope.id === tab.id ? 'active' : ''}
                onClick={() => setActiveScope(tab)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="syllabus-detail-body">
            <div className="syllabus-detail-progress">
              <div className="syllabus-detail-progress-title">
                <Clapperboard size={18} />
                <span>{activeScope.label} Progress</span>
              </div>
              <div className="syllabus-detail-progress-coins">
                <span className="syllabus-detail-coins-label">Total Coins</span>
                <span className="syllabus-detail-coins-value">
                  <Coins size={14} /> {totalCoins} / {maxCoins}
                </span>
              </div>
            </div>
            <div className="syllabus-detail-progress-bar">
              <div className="syllabus-detail-progress-bar-fill" style={{ width: `${Math.min(100, percent)}%` }} />
            </div>
            <div className="syllabus-detail-progress-percent">{percent}%</div>

            <div className="syllabus-detail-table-wrapper">
              <table className="syllabus-detail-table">
                <thead>
                  <tr>
                    <th>{activeScope.prefix}</th>
                    <th>Coins</th>
                    <th>Status</th>
                    <th>Coach Comment</th>
                    <th>Date</th>
                    {canEdit && <th><span className="sr-only">Action</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={columnCount} className="syllabus-empty">Loading...</td></tr>
                  )}
                  {!loading && sessions.length === 0 && (
                    <tr><td colSpan={columnCount} className="syllabus-empty">No content for {activeScope.label} yet.</td></tr>
                  )}
                  {!loading && sessions.map((session) => {
                    const entry = entries.find((e) => e.sessionNumber === session.sessionNumber)
                      || { sessionNumber: session.sessionNumber, score: null, isNotApplicable: false, comment: '', updatedAt: null };
                    const status = getEntryStatus(entry.score, entry.isNotApplicable, session.maxScore);
                    return (
                      <tr key={session.sessionNumber}>
                        <td>
                          {getColumnLabel(activeScope, session)}
                          {rowOf(session) && <div className="syllabus-detail-course">{rowOf(session)}</div>}
                        </td>
                        <td>
                          <span className={`syllabus-score-chip tone-${status.tone}`}>
                            {entry.isNotApplicable ? 'N/A' : entry.score ?? '-'}
                          </span>
                          {!entry.isNotApplicable && entry.ruleScores?.length > 0 && (
                            <div className="syllabus-detail-rule-scores">{ruleScoresText(entry.ruleScores)}</div>
                          )}
                        </td>
                        <td><span className={`syllabus-status-pill tone-${status.tone}`}>{status.label}</span></td>
                        <td className="syllabus-detail-comment-cell">{entry.comment || '-'}</td>
                        <td>{formatEntryDate(entry.updatedAt)}</td>
                        {canEdit && (
                          <td className="syllabus-detail-actions-cell">
                            <button
                              type="button"
                              onClick={() => setEditingSession(session)}
                              aria-label={`Edit ${getColumnLabel(activeScope, session)}`}
                              title="Edit"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(session.sessionNumber)}
                              disabled={deletingSessionNumber === session.sessionNumber}
                              aria-label={`Clear ${getColumnLabel(activeScope, session)}`}
                              title="Clear entry"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="syllabus-detail-remarks">
              <div className="syllabus-detail-remarks-header">
                <MessageSquare size={16} />
                <span>Overall Coach Remarks</span>
                {remarks.updatedAt && (
                  <div className="syllabus-detail-remarks-updated">
                    Last Updated<br />{formatEntryDate(remarks.updatedAt)}
                  </div>
                )}
              </div>
              {canEdit ? (
                <>
                  <textarea
                    value={remarksDraft}
                    onChange={(e) => setRemarksDraft(e.target.value)}
                    placeholder={`Add overall remarks for ${activeScope.label}...`}
                    rows={2}
                  />
                  {remarksDraft !== remarks.text && (
                    <button
                      type="button"
                      className="syllabus-detail-remarks-save"
                      onClick={saveRemarksDraft}
                      disabled={savingRemarks}
                    >
                      {savingRemarks ? 'Saving...' : 'Save Remarks'}
                    </button>
                  )}
                </>
              ) : (
                <div className="syllabus-detail-remarks-text">{remarks.text || 'No remarks yet.'}</div>
              )}
            </div>
          </div>

          <div className="syllabus-detail-footer">
            <button type="button" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>

      {editingSession && (
        <SingleScoreModal
          child={{ studentId, name: studentName, sessions: entries }}
          session={editingSession}
          label={withRow(getColumnLabel(activeScope, editingSession), rowOf(editingSession))}
          maxScore={editingSession.maxScore}
          coinParts={editingSession.coinParts}
          topic={activeTopic}
          month={month}
          year={year}
          balagruhaId={balagruhaId}
          onClose={() => setEditingSession(null)}
          onSaved={refresh}
        />
      )}
    </>
  );
}

// Focused single-cell editor: clicking a score cell (rather than the child's
// name, which opens every session for that child via ChildDetailModal
// above) opens just this one child + this one session, for quickly adding
// or correcting one mark. Reuses the same saveSyllabusEntries upsert with a
// single-entry array, so no backend change was needed for this part.
// `maxScore` = the most coins the session can get (the admin's coins for its
// chapter in Courses but its quiz's, else 30); the server refuses more.
// `coinParts` (the column's) with coin rules: the activity and each rule get
// their own box (CoinPartBlocks), saved as the score and its `ruleScores`.
function SingleScoreModal({ child, session, label, maxScore = MAX_SESSION_SCORE, coinParts, topic, month, year, balagruhaId, onClose, onSaved }) {
  const existing = child.sessions.find((s) => s.sessionNumber === session.sessionNumber);
  const withRules = coinParts?.rules?.length > 0;
  const [score, setScore] = useState(existing?.score ?? '');
  const [split, setSplit] = useState(() => splitScore(existing?.score ?? null, existing?.ruleScores, coinParts));
  const [isNotApplicable, setIsNotApplicable] = useState(existing?.isNotApplicable || false);
  const [comment, setComment] = useState(existing?.comment || '');
  const [saving, setSaving] = useState(false);
  const overLimit = withRules
    ? !isNotApplicable && splitOutOfRange(coinParts, split)
    : score !== '' && Number(score) > maxScore;
  const joined = withRules ? joinScore(split) : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSyllabusEntries({
        balagruhaId,
        topic,
        month,
        year,
        studentId: child.studentId,
        entries: [{
          sessionNumber: session.sessionNumber,
          ...(withRules
            ? { score: isNotApplicable ? null : joined.score, ruleScores: isNotApplicable ? [] : joined.ruleScores }
            : { score: score === '' ? null : Number(score) }),
          isNotApplicable,
          comment,
        }],
      });
      showToast('Score saved', 'success');
      onSaved();
      onClose();
    } catch (error) {
      showToast(error?.response?.data?.message || 'Failed to save score', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full overflow-hidden">
        <div className="syllabus-modal-header">
          <div>
            <h3 className="syllabus-modal-title">{child.name}</h3>
            <div className="syllabus-modal-subtitle">{label}</div>
          </div>
          <button type="button" className="syllabus-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="syllabus-modal-body">
          {withRules ? (
            <>
              <CoinPartBlocks parts={coinParts} split={split} onChange={setSplit} mode="inputs" disabled={isNotApplicable} />
              <div className="syllabus-modal-row-edit syllabus-single-score-row">
                <span className="syllabus-coin-parts-total">
                  Total: {isNotApplicable ? 'N/A' : `${joined.score ?? '-'} / ${maxScore}`} coins
                </span>
                <label className="syllabus-modal-na-toggle">
                  <input
                    type="checkbox"
                    checked={isNotApplicable}
                    onChange={(e) => setIsNotApplicable(e.target.checked)}
                  />
                  N/A
                </label>
              </div>
            </>
          ) : (
            <>
              <div className="syllabus-modal-row-edit syllabus-single-score-row">
                <input
                  type="number"
                  min={0}
                  max={maxScore}
                  value={score}
                  disabled={isNotApplicable}
                  onChange={(e) => setScore(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder={`0-${maxScore}`}
                  aria-label="Coins"
                  autoFocus
                />
                <label className="syllabus-modal-na-toggle">
                  <input
                    type="checkbox"
                    checked={isNotApplicable}
                    onChange={(e) => setIsNotApplicable(e.target.checked)}
                  />
                  N/A
                </label>
              </div>
              {maxScore !== MAX_SESSION_SCORE && !overLimit && (
                <p className="syllabus-score-limit">At most {maxScore} coins (the admin&apos;s limit for this activity).</p>
              )}
              {overLimit && (
                <p className="syllabus-score-limit is-over" role="alert">
                  This activity can get at most {maxScore} coins (the admin&apos;s limit).
                  {isNotApplicable ? ' Untick N/A to lower the score.' : ''}
                </p>
              )}
            </>
          )}
          <QuizCoinsNote parts={coinParts} />
          <div className="syllabus-single-score-comment">
            <label htmlFor="syllabus-single-score-comment">Coach Comment</label>
            <textarea
              id="syllabus-single-score-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              placeholder="Optional comment..."
            />
          </div>
        </div>

        <div className="syllabus-modal-footer">
          <button type="button" className="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving || overLimit}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ the admin's lesson plan
// The Daily Schedule's lesson plan gives each child activities for a visit; the matrix marks them on
// the cells (`assigned`: the latest visit of the month that gave this column, and its `refusal` once
// the coach recorded that the child refused it; `doneInstead`: the planned activities this column was
// done in place of).

// "29 Oct 2026" of a YYYY-MM-DD day.
const planDay = (key) => new Date(`${key}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// `nameOf(sessionNumber)` names the columns a child did instead (`notFollowed`: worked out from the
// scores, when the coach graded another column and not the planned one).
const assignedText = ({ name, date, refusal, notFollowed }, nameOf = () => '') => {
  if (refusal) {
    return `Refused ${name} (assigned by Admin, ${planDay(date)}) · ${refusal.doneName ? `did ${refusal.doneName} instead` : 'did nothing else'}${refusal.reason ? ` · ${refusal.reason}` : ''}`;
  }
  if (notFollowed) {
    return `Not followed: ${name} was assigned by Admin (${planDay(date)}), did ${notFollowed.didSessionNumbers.map(nameOf).join(', ')} instead`;
  }
  return `Assigned by Admin in the Daily Schedule of ${planDay(date)}: ${name}`;
};
const PLAN_TAGS = {
  refused: { Icon: Ban, label: 'Refused', className: 'is-refused' },
  notFollowed: { Icon: AlertCircle, label: 'Not followed', className: 'is-not-followed' },
  assigned: { Icon: ClipboardCheck, label: 'Assigned by Admin', className: 'is-assigned' },
};

// "Assigned by Admin" ("Refused" / "Not followed") on a planned cell, "Instead of …" on the one done in its place.
function PlanCellTags({ child, cell, columnName, nameOf, onOpen }) {
  const { assigned, doneInstead } = cell;
  if (!assigned && !doneInstead?.length) return null;
  const kind = PLAN_TAGS[assigned?.refusal ? 'refused' : assigned?.notFollowed ? 'notFollowed' : 'assigned'];
  const tag = assigned && (
    <>
      <kind.Icon size={12} aria-hidden="true" />
      {kind.label}
    </>
  );
  const className = `syllabus-plan-tag ${kind.className}`;
  const text = assigned && assignedText(assigned, nameOf);
  return (
    <span className="syllabus-plan-tags">
      {assigned && (onOpen ? (
        <button type="button" className={className} title={text} aria-label={`${child.name}, ${columnName}: ${text}`} onClick={onOpen}>
          {tag}
        </button>
      ) : (
        <span className={className} title={text}>{tag}</span>
      ))}
      {doneInstead?.map((d) => (
        <span key={`${d.date}-${d.sessionNumber}`} className="syllabus-plan-tag is-instead" title={`Done instead of ${d.name} (assigned by Admin, ${planDay(d.date)})`}>
          <Repeat size={12} aria-hidden="true" />
          <span className="syllabus-plan-tag-text">Instead of {d.name}</span>
        </span>
      ))}
    </span>
  );
}

// Opened from "Assigned by Admin" / "Refused" / "Not followed": the child refused the planned activity, and what
// they did instead (another activity of the view, or nothing). Saved for the admin to see; Undo removes
// it. When the scores already show another activity ("Not followed"), it starts with that one.
function PlanRefusalModal({ child, cell, label, options, balagruhaId, topic, onClose, onSaved }) {
  const { assigned } = cell;
  const refusal = assigned.refusal;
  const autoDone = assigned.notFollowed?.didSessionNumbers || [];
  const [done, setDone] = useState(() => {
    const first = refusal ? refusal.doneSessionNumber : autoDone[0];
    return first ? String(first) : '';
  });
  const [reason, setReason] = useState(refusal?.reason || '');
  const [saving, setSaving] = useState(false);
  const target = { balagruhaId, studentId: child.studentId, date: assigned.date, topic, sessionNumber: cell.sessionNumber };

  const run = async (work, message) => {
    setSaving(true);
    try {
      await work();
      showToast(message, 'success');
      onSaved();
      onClose();
    } catch (error) {
      showToast(error?.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };
  const save = () => {
    const picked = options.find((o) => String(o.sessionNumber) === done);
    return run(
      () => savePlanRefusal({ ...target, doneSessionNumber: picked?.sessionNumber ?? null, doneName: picked?.label || '', reason: reason.trim() }),
      'Refusal saved. The admin can see it.'
    );
  };
  const undo = () => run(() => deletePlanRefusal(target), 'Refusal removed');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="syllabus-plan-title">
        <div className="syllabus-modal-header">
          <div>
            <h3 id="syllabus-plan-title" className="syllabus-modal-title">{child.name}</h3>
            <div className="syllabus-modal-subtitle">{label}</div>
          </div>
          <button type="button" className="syllabus-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="syllabus-modal-body">
          <p className="syllabus-plan-note">
            <ClipboardCheck size={16} aria-hidden="true" />
            <span>
              Assigned by Admin in the Daily Schedule of <b>{planDay(assigned.date)}</b>: <b>{assigned.value || assigned.name}</b>
            </span>
          </p>
          {refusal && (
            <p className="syllabus-plan-note is-refused">
              <Ban size={16} aria-hidden="true" />
              <span>
                {child.name} refused it{refusal.doneName ? ` and did ${refusal.doneName} instead` : ''}.
                {refusal.recordedBy && <small> Recorded by {refusal.recordedBy}{refusal.recordedAt ? ` on ${formatEntryDate(refusal.recordedAt)}` : ''}.</small>}
              </span>
            </p>
          )}
          {!refusal && autoDone.length > 0 && (
            <p className="syllabus-plan-note is-not-followed">
              <AlertCircle size={16} aria-hidden="true" />
              <span>
                Not followed: {child.name} did {autoDone.map((n) => options.find((o) => o.sessionNumber === n)?.label || `activity ${n}`).join(', ')} instead
                {assigned.notFollowed.at ? ` (scored on ${formatEntryDate(assigned.notFollowed.at)})` : ''}. The admin can see this.
              </span>
            </p>
          )}
          <h4 className="syllabus-plan-question">
            {refusal ? 'Change what was recorded' : autoDone.length ? 'Add a reason (optional)' : `Did ${child.name} refuse it?`}
          </h4>
          <div className="syllabus-single-score-comment">
            <label htmlFor="syllabus-plan-done">Did instead</label>
            <select id="syllabus-plan-done" className="syllabus-plan-select" value={done} onChange={(e) => setDone(e.target.value)}>
              <option value="">Nothing else</option>
              {options.map((o) => (
                <option key={o.sessionNumber} value={String(o.sessionNumber)}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="syllabus-single-score-comment">
            <label htmlFor="syllabus-plan-reason">Reason (optional)</label>
            <textarea
              id="syllabus-plan-reason"
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Not comfortable with this song"
            />
          </div>
        </div>

        <div className="syllabus-modal-footer">
          <button type="button" className="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          {refusal && (
            <button type="button" className="ghost" onClick={undo} disabled={saving}>
              Undo refusal
            </button>
          )}
          <button type="button" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : refusal ? 'Save changes' : autoDone.length ? 'Save' : 'Child refused'}
          </button>
        </div>
      </div>
    </div>
  );
}

// The media icon next to a cell's score chip, once the child has sent practice
// for the session: how many they sent when it's more than one, and an unread
// dot while any of them is new.
function SubmissionCellButton({ child, submission, onOpen }) {
  const count = submission.count || 1;
  const isNew = submission.status === 'pending' || submission.pendingCount > 0;
  return (
    <button
      type="button"
      className={`syllabus-submission-icon-btn type-${submission.submissionType} ${isNew ? 'has-dot' : ''}`}
      onClick={onOpen}
      aria-label={count > 1
        ? `View and grade ${child.name}'s ${count} submissions`
        : `View and grade ${child.name}'s ${submission.submissionType} submission`}
      title={count > 1 ? `View & grade ${count} submissions` : 'View & grade submission'}
    >
      <SubmissionTypeIcon type={submission.submissionType} size={13} />
      {count > 1 && <span className="syllabus-submission-count" aria-hidden="true">{count}</span>}
    </button>
  );
}

// Submission review + grade modal: opened from the media icon next to a
// score chip (only shown once a child has submitted practice for that
// session). Fetches the submission's own detail (presigned media URL, note,
// timestamp) on open rather than reusing the matrix row, since those fields
// are too heavy/short-lived to embed in every cell of the grid. "Save Grade"
// writes through to the same SyllabusEntry a manually-typed score would.
// `maxScore` = the most coins the session can get (its admin limit, else 30).
// `submissions` = every practice the child sent for the session, newest first
// (the cell's list): with more than one, each can be picked and played. The
// grade is for the session, saved through the newest (`submissionId`), and it
// marks all of them reviewed.
// `coinParts` (the column's) with coin rules: the activity and each rule
// (e.g. Expression) get their own block (CoinPartBlocks), saved as the score
// and its `ruleScores`; its quiz's coins are only noted (the child's).
function SubmissionReviewModal({ submissionId, submissions, studentName, sessionLabel, maxScore = MAX_SESSION_SCORE, coinParts, initialScore, initialRuleScores, initialComment, onClose, onSaved }) {
  const list = submissions?.length ? submissions : [{ submissionId }];
  const withRules = coinParts?.rules?.length > 0;
  const [viewingId, setViewingId] = useState(submissionId);
  // submissionId -> its detail, or null when it couldn't be loaded.
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const submission = details[viewingId];
  const ready = Boolean(details[submissionId]);
  const [single, setScore] = useState(initialScore ?? null);
  const [split, setSplit] = useState(() => splitScore(initialScore ?? null, initialRuleScores, coinParts));
  const joined = withRules ? joinScore(split) : null;
  const score = withRules ? joined.score : single;
  const [comment, setComment] = useState(initialComment || '');
  const [saving, setSaving] = useState(false);
  const overLimit = withRules ? splitOutOfRange(coinParts, split) : score !== null && score > maxScore;
  const invalidScore = score !== null && (Number.isNaN(score) || score < 0);
  // Typed coins must be whole numbers (the buttons always are).
  const notWhole = (withRules ? [split.activity, ...Object.values(split.rules)] : [score]).some(
    (value) => typeof value === 'number' && !Number.isNaN(value) && !Number.isInteger(value)
  );

  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(details, viewingId)) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getSyllabusSubmission(viewingId);
        if (!cancelled) setDetails((prev) => ({ ...prev, [viewingId]: res?.data?.submission || null }));
      } catch (error) {
        if (!cancelled) {
          showToast('Failed to load submission', 'error');
          setDetails((prev) => ({ ...prev, [viewingId]: null }));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingId]);

  const handleSave = async () => {
    if (score === null || saving) return;
    setSaving(true);
    try {
      await gradeSyllabusSubmission(submissionId, withRules ? { score, comment, ruleScores: joined.ruleScores } : { score, comment });
      showToast('Grade saved', 'success');
      onSaved();
      onClose();
    } catch (error) {
      showToast(error?.response?.data?.message || 'Failed to save grade', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="syllabus-submission-modal">
        <div className="syllabus-modal-header">
          <div>
            <h3 className="syllabus-modal-title">{studentName} &middot; {sessionLabel}</h3>
          </div>
          <button type="button" className="syllabus-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="syllabus-modal-body">
          {!ready ? (
            <div className="syllabus-modal-empty">{loading ? 'Loading...' : 'Submission not found.'}</div>
          ) : (
            <>
              {list.length > 1 && (
                <div className="syllabus-submission-list">
                  <div className="syllabus-submission-list-title">
                    {list.length} submissions for this activity
                  </div>
                  <div className="syllabus-submission-list-items" role="group" aria-label="Submissions">
                    {list.map((item, index) => (
                      <button
                        key={item.submissionId}
                        type="button"
                        className={viewingId === item.submissionId ? 'active' : ''}
                        aria-pressed={viewingId === item.submissionId}
                        onClick={() => setViewingId(item.submissionId)}
                      >
                        <SubmissionTypeIcon type={item.submissionType} size={13} />
                        <span className="syllabus-submission-list-name">
                          Submission {list.length - index}{index === 0 ? ' (latest)' : ''}
                        </span>
                        <span className="syllabus-submission-list-time">{formatSubmissionTimestamp(item.submittedAt)}</span>
                        {item.status === 'pending' && <span className="syllabus-submission-new">New</span>}
                      </button>
                    ))}
                  </div>
                  <p className="syllabus-submission-list-hint">
                    One grade for the activity: Save Grade marks all {list.length} submissions as reviewed.
                  </p>
                </div>
              )}

              <div className="syllabus-submission-media-row">
                {submission ? (
                  <>
                    <div className="syllabus-submission-media">
                      {submission.submissionType === 'video' && submission.mediaUrl && (
                        <video key={submission._id} src={submission.mediaUrl} controls className="syllabus-submission-video" />
                      )}
                      {submission.submissionType === 'audio' && submission.mediaUrl && (
                        <audio key={submission._id} src={submission.mediaUrl} controls className="syllabus-submission-audio" />
                      )}
                      {submission.submissionType === 'image' && submission.mediaUrl && (
                        <img src={submission.mediaUrl} alt="Submission" className="syllabus-submission-image" />
                      )}
                      {!submission.mediaUrl && (
                        <div className="syllabus-modal-empty">Media unavailable.</div>
                      )}
                    </div>
                    <div className="syllabus-submission-meta">
                      <div className="syllabus-submission-type-label">
                        <SubmissionTypeIcon type={submission.submissionType} size={14} />
                        {SUBMISSION_TYPE_LABEL[submission.submissionType] || 'Media'} {submission.recordedByCoach ? 'recorded by coach' : 'Submission'}
                      </div>
                      <div className="syllabus-submission-timestamp">{formatSubmissionTimestamp(submission.submittedAt)}</div>
                      {submission.note && <p className="syllabus-submission-note">{submission.note}</p>}
                    </div>
                  </>
                ) : (
                  <div className="syllabus-modal-empty">{loading ? 'Loading...' : 'Submission not found.'}</div>
                )}
              </div>

              <div className="syllabus-submission-grade-section">
                <label className="syllabus-submission-grade-label">Give Grade / Coins</label>
                {withRules ? (
                  <>
                    <CoinPartBlocks parts={coinParts} split={split} onChange={setSplit} mode="buttons" />
                    <p className="syllabus-coin-parts-total">
                      Total: {score ?? '-'} / {maxScore} coins
                    </p>
                  </>
                ) : (
                  <>
                    <div className="syllabus-submission-grade-options">
                      {gradeCoinOptions(maxScore).map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={score === value ? 'active' : ''}
                          onClick={() => setScore(value)}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                    {/* The buttons go in steps, so any amount up to the limit can be typed too. */}
                    <label className="syllabus-submission-grade-custom">
                      <span>Or type the coins</span>
                      <input
                        type="number"
                        min={0}
                        max={maxScore}
                        step={1}
                        value={score ?? ''}
                        onChange={(e) => setScore(e.target.value === '' ? null : Number(e.target.value))}
                        placeholder={`0-${maxScore}`}
                        aria-label="Coins"
                      />
                    </label>
                    {maxScore !== MAX_SESSION_SCORE ? (
                      <p className={`syllabus-score-limit${overLimit ? ' is-over' : ''}`}>
                        At most {maxScore} coins (the admin&apos;s limit for this activity).
                      </p>
                    ) : (
                      overLimit && (
                        <p className="syllabus-score-limit is-over" role="alert">
                          This activity can get at most {maxScore} coins.
                        </p>
                      )
                    )}
                  </>
                )}
                {/* With coin rules, a block below 0 already says so under it. */}
                {((!withRules && invalidScore) || notWhole) && (
                  <p className="syllabus-score-limit is-over" role="alert">Type a whole number of coins, 0 or more.</p>
                )}
                <QuizCoinsNote parts={coinParts} />
              </div>

              <div className="syllabus-single-score-comment">
                <label htmlFor="syllabus-submission-comment">Add Comment (optional)</label>
                <textarea
                  id="syllabus-submission-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  placeholder="Optional comment..."
                />
              </div>
            </>
          )}
        </div>

        <div className="syllabus-modal-footer">
          <button type="button" className="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !ready || score === null || overLimit || invalidScore || notWhole}>
            {saving ? 'Saving...' : 'Save Grade'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Syllabus Tracker: a gradebook-style grid of children x sessions for a
// chosen Topic + Balagruha + Month/Year, with per-child View/Edit of
// manually-entered scores. The sessions are the course content the admin
// published there (one per chapter, e.g. "Action Song 1"), so coaches don't
// add or rename them; an older column still shows while it holds marks.
//
// The Topic is picked as a Category › Subcategory of the admin Courses page:
// the admin's course categories (one added later shows too) and their rows
// (English › Action Songs › Vocal Songs, Stories › Saint Stories ...); see
// utils/syllabusTrackerScopes.js for what each pick shows.
//
// Two ways to scope the Balagruha:
//  - Coach dashboard (default): pass `balagruhas` (the coach's assigned
//    Balagruhas) and `selectedBalagruhas` for the default pick; the
//    component renders its own Balgruha dropdown.
//  - Admin dashboard: pass `balagruhaId` directly (the admin's already-
//    selected Balagruha) — this hides the internal dropdown and locks the
//    fetch to that id.
export default function SyllabusTracker({
  balagruhas,
  selectedBalagruhas,
  balagruhaId: balagruhaIdProp,
  canEdit = false,
}) {
  const isBalagruhaLocked = !!balagruhaIdProp;

  const [balagruhaId, setBalagruhaId] = useState(
    balagruhaIdProp || selectedBalagruhas?.[0] || balagruhas?.[0]?._id || ''
  );
  // The built-in Learning Zone rows are there straight away; the admin's
  // categories, the rows admins added (and published courses' task types)
  // arrive with the request below.
  const [subcategoryRows, setSubcategoryRows] = useState({ subcategories: [], taskTypes: [], categories: [], hiddenRows: [] });
  const categories = useMemo(
    () => pickerCategories(buildSubcategoryNodes(subcategoryRows), subcategoryRows.categories),
    [subcategoryRows]
  );
  // Opens on the first topic's own view (English › Action Songs), as the Topic dropdown did.
  const [pick, setPick] = useState(
    () => findTopicPick(pickerCategories(buildSubcategoryNodes()), SYLLABUS_TOPICS[0]) || { category: '', path: [] }
  );
  const monthYearOptions = useMemo(() => getMonthYearOptions(), []);
  const [monthYear, setMonthYear] = useState(monthYearOptions[0]?.value || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [sessionCount, setSessionCount] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [children, setChildren] = useState([]);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null); // { child, session }
  const [reviewingSubmission, setReviewingSubmission] = useState(null); // { child, session, submissionId }
  const [planCell, setPlanCell] = useState(null); // { child, session }: an admin-planned cell's refusal
  const [recordingCell, setRecordingCell] = useState(null); // { child, session }: Practice record, before grading

  useEffect(() => {
    let cancelled = false;
    getSyllabusSubcategories()
      .then((res) => {
        if (!cancelled) {
          setSubcategoryRows({
            subcategories: res?.data || [],
            taskTypes: res?.taskTypes || [],
            categories: Array.isArray(res?.categories) ? res.categories : [],
            // Built-in rows the admin deleted on the Courses page, left out of the pickers.
            hiddenRows: res?.hiddenRows || [],
          });
        }
      })
      // Without them the pickers still list the built-in categories and rows.
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const pickedCategory = categories.find((c) => c.name === pick.category);
  const category = pickedCategory || categories[0];
  let chain = chainFor(category, pickedCategory ? pick.path : []);
  if (!chain.length && category && !category.topic) chain = chainFor(category, defaultPath(category));
  const scope = scopeFor(chain, category?.topic, category?.name) || topicScope(SYLLABUS_TOPICS[0]);
  const { topic } = scope;
  const pickerSelects = pickerLevels(category, chain);
  const categoryTabs = useMemo(() => categoryScopes(category), [category]);

  const handleCategoryChange = (name) => {
    setPick({ category: name, path: defaultPath(categories.find((c) => c.name === name)) });
  };

  const handleSubcategoryChange = (levelIndex, key) => {
    const above = chain.slice(0, levelIndex).map((node) => node.key);
    setPick({ category: category.name, path: key ? [...above, key] : above });
  };

  useEffect(() => {
    if (balagruhaIdProp) {
      setBalagruhaId(balagruhaIdProp);
      return;
    }
    if (!balagruhaId && (selectedBalagruhas?.[0] || balagruhas?.[0]?._id)) {
      setBalagruhaId(selectedBalagruhas?.[0] || balagruhas?.[0]?._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balagruhaIdProp, selectedBalagruhas, balagruhas]);

  const selected = monthYearOptions.find((o) => o.value === monthYear) || monthYearOptions[0];

  // Resolves to the children loaded (none when it failed), for a caller that needs the fresh cells.
  const fetchMatrix = async () => {
    if (!balagruhaId || !topic || !selected) {
      setChildren([]);
      setSessionCount(0);
      setSessions([]);
      setSubmissionsCount(0);
      return [];
    }
    setLoading(true);
    try {
      const response = await getSyllabusMatrix({
        balagruhaId,
        topic,
        month: selected.month,
        year: selected.year,
        subcategories: scope.subcategories,
      });
      const count = response?.data?.sessionCount || 0;
      setChildren(response?.data?.children || []);
      setSessionCount(count);
      setSessions(
        response?.data?.sessions
        || Array.from({ length: count }, (_, i) => ({ sessionNumber: i + 1, label: '' }))
      );
      setSubmissionsCount(response?.data?.submissionsCount || 0);
      return response?.data?.children || [];
    } catch (error) {
      showToast('Failed to load syllabus data', 'error');
      setChildren([]);
      setSessionCount(0);
      setSessions([]);
      setSubmissionsCount(0);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatrix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balagruhaId, scope.id, monthYear]);

  useEffect(() => {
    setCurrentPage(1);
  }, [balagruhaId, scope.id, monthYear, statusFilter, searchText]);

  const counts = children.reduce((acc, child) => {
    acc[child.status] = (acc[child.status] || 0) + 1;
    return acc;
  }, {});

  const hasPendingSubmissions = children.some((child) =>
    child.sessions.some((s) => s.submission && (s.submission.status === 'pending' || s.submission.pendingCount > 0))
  );

  const searchLower = searchText.trim().toLowerCase();
  const filteredChildren = children.filter((child) => {
    if (statusFilter === 'submissions') {
      if (!child.sessions.some((s) => s.submission)) return false;
    } else if (statusFilter !== 'all' && child.status !== statusFilter) {
      return false;
    }
    if (searchLower && !(child.name || '').toLowerCase().includes(searchLower)) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredChildren.length / ITEMS_PER_PAGE));
  const pageSafe = Math.min(currentPage, totalPages);
  const paginatedChildren = filteredChildren.slice(
    (pageSafe - 1) * ITEMS_PER_PAGE,
    pageSafe * ITEMS_PER_PAGE
  );

  const balagruhaName = (balagruhas || []).find((b) => b._id === balagruhaId)?.name || '';
  // Each column's most coins (its admin limit, else 30), for the score colours.
  const maxScoreOf = (sessionNumber) => sessions.find((s) => s.sessionNumber === sessionNumber)?.maxScore ?? MAX_SESSION_SCORE;
  // What a column's admin coins are for (activity, coin rules, quiz), when the admin gave some.
  const coinPartsOf = (sessionNumber) => sessions.find((s) => s.sessionNumber === sessionNumber)?.coinParts;
  const rowOf = repeatedRows(scope, sessions);
  // A column's full name: "Action Song 1", or "Action Song 1 · Vocal Songs" when the name repeats.
  const columnName = (sessionNumber, fallback) => {
    const column = sessions.find((s) => s.sessionNumber === sessionNumber) || fallback;
    return withRow(getColumnLabel(scope, column), rowOf(column));
  };

  // Practice record's lines under the child's name: "Action Songs · Action Song 2 · Vocal Songs" and
  // "October 2026 · English · Not Started".
  const recordDetails = ({ session }) => {
    const column = sessions.find((s) => s.sessionNumber === session.sessionNumber) || session;
    return [
      [scope.label, getColumnLabel(scope, column), column.rowLabel !== scope.label && column.rowLabel].filter(Boolean).join(' · '),
      [
        selected ? formatMonthYearLabel(selected.month, selected.year) : '',
        category?.name !== scope.label && category?.name,
        getEntryStatus(session.score, session.isNotApplicable, maxScoreOf(session.sessionNumber)).label,
      ].filter(Boolean).join(' · '),
    ];
  };
  // After Practice record: with the coach's recording saved, its grading opens, as for a child's
  // practice (Save Grade marks it reviewed); without one, the cell's score editor, as before.
  const gradeRecorded = async (submission) => {
    const { child, session } = recordingCell;
    setRecordingCell(null);
    if (!submission?._id) {
      setSelectedCell({ child, session });
      return;
    }
    const fresh = (await fetchMatrix()).find((c) => c.studentId === child.studentId) || child;
    const cell = fresh.sessions?.find((s) => s.sessionNumber === session.sessionNumber) || session;
    setReviewingSubmission({ child: fresh, session: cell, submissionId: submission._id });
  };

  const handleExportExcel = () => {
    const subtitleParts = [selected ? formatMonthYearLabel(selected.month, selected.year) : '', balagruhaName].filter(Boolean);

    const tableColumn = [
      'Sr No',
      'Child Name',
      ...sessions.map((s) => withRow(getColumnLabel(scope, s), rowOf(s))),
    ];
    const tableRows = filteredChildren.map((child, index) => [
      index + 1,
      child.name,
      ...child.sessions.map((s) => (s.isNotApplicable ? 'N/A' : s.score ?? '-')),
    ]);

    const sheetData = [
      [`Syllabus Tracker - ${scope.title}`],
      [subtitleParts.join(' - ')],
      [],
      tableColumn,
      ...tableRows,
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet['!cols'] = tableColumn.map((_, i) => ({ wch: i === 1 ? 22 : 14 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Syllabus Tracker');

    XLSX.writeFile(workbook, `Syllabus_Tracker_${scope.title.replace(/\s*›\s*/g, '-').replace(/\s+/g, '_')}_${monthYear}.xlsx`);
    showToast('Excel file exported successfully', 'success');
  };

  return (
    <div className="syllabus-tracker">
      <div className="syllabus-header">
        <div className="syllabus-title">
          <span className="syllabus-title-icon" aria-hidden="true">
            <BookOpen size={24} color="#3a6df0" strokeWidth={2.25} />
          </span>
          <div>
            <h3>Syllabus Tracker</h3>
            <div className="syllabus-subtitle">Track Learning &middot; Build Skills &middot; Earn Coins</div>
          </div>
        </div>

        <div className="syllabus-header-controls">
          {!isBalagruhaLocked && (
            <div className="syllabus-context-pill">
              <span className="syllabus-context-label">Balgruha</span>
              <SearchableSelect
                searchPlaceholder="Search balagruha..."
                className="syllabus-context-select"
                value={balagruhaId}
                onChange={(e) => setBalagruhaId(e.target.value)}
              >
                {(balagruhas || []).map((b) => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </SearchableSelect>
            </div>
          )}

          <div className="syllabus-context-pill">
            <select
              className="syllabus-context-select"
              value={monthYear}
              onChange={(e) => setMonthYear(e.target.value)}
            >
              {monthYearOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="syllabus-export-btn"
            onClick={handleExportExcel}
            disabled={filteredChildren.length === 0}
          >
            <Download size={16} /> Export Excel
          </button>
        </div>
      </div>

      <div className="syllabus-topic-row">
        <div className="syllabus-topic-field">
          <label htmlFor="syllabus-category-select" className="syllabus-topic-label">Category</label>
          <select
            id="syllabus-category-select"
            className="syllabus-topic-select syllabus-topic-select--category"
            value={category?.name || ''}
            onChange={(e) => handleCategoryChange(e.target.value)}
          >
            {categories.map((c) => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>
        {pickerSelects.length > 0 && (
          // One field for every level of the subcategory tree, so it gets a share of the row per level.
          <div className="syllabus-topic-field syllabus-topic-field--subcategory" style={{ '--levels': pickerSelects.length }}>
            <label htmlFor="syllabus-subcategory-select" className="syllabus-topic-label">Subcategory</label>
            <div className="syllabus-topic-selects">
              {pickerSelects.map((level, index) => (
                <SearchableSelect
                  searchPlaceholder="Search subcategory..."
                  key={index}
                  id={index === 0 ? 'syllabus-subcategory-select' : undefined}
                  aria-label={index === 0 ? undefined : `Under ${level.parentLabel}`}
                  className="syllabus-topic-select"
                  value={level.value}
                  onChange={(e) => handleSubcategoryChange(index, e.target.value)}
                >
                  {level.emptyLabel && <option value="">{level.emptyLabel}</option>}
                  {level.nodes.map((node) => (
                    <option key={node.key} value={node.key}>{node.label}</option>
                  ))}
                </SearchableSelect>
              ))}
            </div>
          </div>
        )}
        <div className="syllabus-topic-field syllabus-topic-field--search">
          <label htmlFor="syllabus-search-input" className="syllabus-topic-label">Search</label>
          <div className="syllabus-search">
            <Search size={16} className="syllabus-search-icon" aria-hidden="true" />
            <input
              id="syllabus-search-input"
              type="text"
              placeholder="Search child name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="syllabus-toolbar">
        <div className="syllabus-filter-tabs">
          {STATUS_OPTIONS.map((option) => {
            const TabIcon = STATUS_ICON[option.id];
            return (
              <button
                key={option.id}
                type="button"
                className={`syllabus-filter-tab tone-${option.id} ${statusFilter === option.id ? 'active' : ''}`}
                onClick={() => setStatusFilter(option.id)}
              >
                <TabIcon size={14} />
                {option.label}{option.id !== 'all' ? ` (${counts[option.id] || 0})` : ''}
              </button>
            );
          })}
          <button
            type="button"
            className={`syllabus-filter-tab tone-submissions ${statusFilter === 'submissions' ? 'active' : ''}`}
            onClick={() => setStatusFilter('submissions')}
          >
            <Video size={14} />
            Submissions ({submissionsCount})
            {hasPendingSubmissions && <span className="syllabus-filter-tab-dot" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {statusFilter === 'submissions' && (
        <div className="syllabus-submissions-banner">
          <Info size={16} />
          <span>Showing only children with submissions. Click on the video/audio icon to view, play and grade.</span>
          <button type="button" className="syllabus-submissions-clear" onClick={() => setStatusFilter('all')}>
            <X size={13} /> Clear Filter
          </button>
        </div>
      )}

      {!loading && children.length > 0 && sessions.length === 0 && (
        <div className="syllabus-submissions-banner" role="status">
          <Info size={16} />
          <span>No content in {scope.title} yet. Its activities show here once the admin adds content to it in Courses.</span>
        </div>
      )}

      <div className="syllabus-card">
        <div className="syllabus-table-wrapper">
          <table className="syllabus-table">
            <thead>
              <tr>
                <th>Sr No</th>
                <th>Child Name</th>
                {sessions.map((session) => (
                  <th key={session.sessionNumber}>
                    <span className="syllabus-session-label">{getColumnLabel(scope, session)}</span>
                    {rowOf(session) && (
                      <span className="syllabus-session-course" title={`In ${rowOf(session)}`}>
                        {rowOf(session)}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={sessionCount + 2} className="syllabus-empty">Loading...</td></tr>
              )}
              {!loading && filteredChildren.length === 0 && (
                <tr><td colSpan={sessionCount + 2} className="syllabus-empty">No children found</td></tr>
              )}
              {!loading && paginatedChildren.map((child, index) => (
                <tr key={child.studentId} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                  <td>{(pageSafe - 1) * ITEMS_PER_PAGE + index + 1}</td>
                  <td>
                    <button
                      type="button"
                      className="syllabus-child-name"
                      onClick={() => setSelectedChild(child)}
                    >
                      {child.name}
                    </button>
                  </td>
                  {child.sessions.map((session) => (
                    <td key={session.sessionNumber}>
                      <div className="syllabus-cell-content">
                        {canEdit ? (
                          // Opens Practice record (record audio / video, then grade). Not scored yet: a
                          // coins icon in place of "-".
                          <button
                            type="button"
                            className={`syllabus-score-chip syllabus-score-chip-btn tone-${getScoreBand(session.score, session.isNotApplicable, maxScoreOf(session.sessionNumber))}${isUnscored(session) ? ' syllabus-score-chip--give' : ''}`}
                            onClick={() => setRecordingCell({ child, session })}
                            {...(isUnscored(session)
                              ? { 'aria-label': `Give coins to ${child.name} for ${columnName(session.sessionNumber, session)}`, title: 'Give coins' }
                              : {})}
                          >
                            {isUnscored(session) ? <Coins size={16} aria-hidden="true" /> : session.isNotApplicable ? 'N/A' : session.score}
                          </button>
                        ) : (
                          <span className={`syllabus-score-chip tone-${getScoreBand(session.score, session.isNotApplicable, maxScoreOf(session.sessionNumber))}`}>
                            {session.isNotApplicable ? 'N/A' : session.score ?? '-'}
                          </span>
                        )}
                        {canEdit && session.submission && (
                          <SubmissionCellButton
                            child={child}
                            submission={session.submission}
                            onOpen={() => setReviewingSubmission({ child, session, submissionId: session.submission.submissionId })}
                          />
                        )}
                        <PlanCellTags
                          child={child}
                          cell={session}
                          columnName={columnName(session.sessionNumber, session)}
                          nameOf={(n) => columnName(n, { sessionNumber: n })}
                          onOpen={canEdit && session.assigned ? () => setPlanCell({ child, session }) : null}
                        />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="syllabus-footer">
          <div className="syllabus-footer-info">
            {filteredChildren.length === 0
              ? 'Showing 0 children'
              : `Showing ${(pageSafe - 1) * ITEMS_PER_PAGE + 1} to ${Math.min(pageSafe * ITEMS_PER_PAGE, filteredChildren.length)} of ${filteredChildren.length} children`}
          </div>
          {totalPages > 1 && (
            <div className="syllabus-pagination">
              <button type="button" disabled={pageSafe === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>‹</button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={pageSafe === i + 1 ? 'active' : ''}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button type="button" disabled={pageSafe === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>›</button>
            </div>
          )}
        </div>
      </div>

      {selectedChild && (
        <ChildDetailModal
          studentId={selectedChild.studentId}
          studentName={selectedChild.name}
          balagruhaName={balagruhaName}
          balagruhaId={balagruhaId}
          initialScope={scope}
          outerScope={scope}
          categoryTabs={categoryTabs}
          month={selected?.month}
          year={selected?.year}
          canEdit={canEdit}
          onClose={() => setSelectedChild(null)}
          onSaved={fetchMatrix}
        />
      )}

      {selectedCell && (
        <SingleScoreModal
          child={selectedCell.child}
          session={selectedCell.session}
          label={columnName(selectedCell.session.sessionNumber, selectedCell.session)}
          maxScore={maxScoreOf(selectedCell.session.sessionNumber)}
          coinParts={coinPartsOf(selectedCell.session.sessionNumber)}
          topic={topic}
          month={selected?.month}
          year={selected?.year}
          balagruhaId={balagruhaId}
          onClose={() => setSelectedCell(null)}
          onSaved={fetchMatrix}
        />
      )}

      {recordingCell && (
        <PracticeRecordModal
          child={recordingCell.child}
          sessionNumber={recordingCell.session.sessionNumber}
          details={recordDetails(recordingCell)}
          scope={{ balagruhaId, topic, month: selected?.month, year: selected?.year }}
          onClose={() => setRecordingCell(null)}
          onGrade={gradeRecorded}
        />
      )}

      {planCell && (
        <PlanRefusalModal
          child={planCell.child}
          cell={planCell.session}
          label={columnName(planCell.session.sessionNumber, planCell.session)}
          options={sessions
            .filter((s) => s.sessionNumber !== planCell.session.sessionNumber)
            .map((s) => ({ sessionNumber: s.sessionNumber, label: columnName(s.sessionNumber, s) }))}
          balagruhaId={balagruhaId}
          topic={topic}
          onClose={() => setPlanCell(null)}
          onSaved={fetchMatrix}
        />
      )}

      {reviewingSubmission && (
        <SubmissionReviewModal
          submissionId={reviewingSubmission.submissionId}
          submissions={reviewingSubmission.session.submission?.submissions}
          studentName={reviewingSubmission.child.name}
          sessionLabel={columnName(reviewingSubmission.session.sessionNumber, reviewingSubmission.session)}
          maxScore={maxScoreOf(reviewingSubmission.session.sessionNumber)}
          coinParts={coinPartsOf(reviewingSubmission.session.sessionNumber)}
          initialScore={reviewingSubmission.session.score}
          initialRuleScores={reviewingSubmission.session.ruleScores}
          initialComment={reviewingSubmission.session.comment}
          onClose={() => setReviewingSubmission(null)}
          onSaved={fetchMatrix}
        />
      )}
    </div>
  );
}
